import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';

import { Box, Typography } from '@material-ui/core';
import { ToastContainer, toast } from 'react-toastify';

import { brandConsumersAlgorithmCalculation } from '../../../redux/actions/Firebase';
import { makeURL } from '../../../helperFunctions';

import PageContainer from '../../../myComponents/pageContainer';
import PageLoader from '../../../myComponents/loaders/pageLoader';
import ChartCalculations from '../CompetitiveSetBrands/chartCalculations';

import HeaderSelector from '../../../myComponents/headerSelector';
import Filters from '../../../myComponents/filters/Filters';
import PDFExportButton from '../../../myComponents/PDFExportButton';
import UserDropdownHeader from '../../../myComponents/userDropdownHeader';
import BuyerBatchSelector from '../../../myComponents/buyerBatchSelector';
import { DEFAULT_BATCH_COUNTRY } from '../../../config/batchConfig';

const BrandConsumersDashboard = () => {
  const { brandName, batch } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const appData = useSelector(({ data }) => data);
  const [brandPickedId, setBrandPickedId] = useState(null);
  const PDFContentRef = useRef(null);
  const [pickedBatchData, setPickedBatchData] = useState(null);
  const [chosenFilters, setChosenFilters] = useState(null);
  const [pickedBatchCountry, setPickedBatchCountry] = useState(DEFAULT_BATCH_COUNTRY);

  const [pageIsLoading, setPageIsLoading] = useState(true);
  const [useEffectShouldTrigger, setUseEffectShouldTrigger] = useState(true);

  //Calculation state
  const [calculations, setCalculations] = useState({
    loading: false,
    results: null,
  });

  const [pickedBrand, setPickedBrand] = useState(null);

  useEffect(() => {
    if (!appData.loading && useEffectShouldTrigger) {
      setUseEffectShouldTrigger(false);
      if (appData.error) {
        showToastWarn();
      } else {
        if (!brandName || !batch) {
          history.push(makeURL('/brand-consumers/all'));
        } else {
          const splitBatch = batch.split('_');
          const quarterWithIncrement = `${splitBatch[1]}_${splitBatch[4]}`;
          const batchYear = splitBatch[2];
          const batchCountry = splitBatch[3];
          const validPickedBrand = !appData.yourBrandAndConsumersData?.[batchCountry]
            ? false
            : Object.keys(appData.yourBrandAndConsumersData[batchCountry]).find(e => {
                const chosenBrandBatchData = { ...appData.yourBrandAndConsumersData[batchCountry][e] };
                return (
                  e === brandName &&
                  chosenBrandBatchData[batchYear] &&
                  chosenBrandBatchData[batchYear].includes(quarterWithIncrement)
                );
              });
          if (!validPickedBrand) return history.push(makeURL('/brand-consumers/all'));

          const pickedBatchToSet = {
            quarter: [quarterWithIncrement],
            year: batchYear,
          };
          setPickedBatchData(pickedBatchToSet);
          setPickedBatchCountry(batchCountry);

          brandChangeHandler(validPickedBrand, pickedBatchToSet, batchCountry);
          setPageIsLoading(false);
        }
      }
    }
  }, [appData.loading, brandName, batch]);

  useEffect(() => {
    if (brandPickedId) {
      history.push(makeURL(`/brand-consumers/${brandPickedId}`));
      setUseEffectShouldTrigger(true);
    }
  }, [brandPickedId]);

  const calculateHandler = (userPickedBrand, filters, newPickedBatchData, newPickedBatchCountry) => {
    setCalculations(prevState => ({ ...prevState, loading: true }));
    brandConsumersAlgorithmCalculation(
      {
        brandName: userPickedBrand,
        filters: filters,
        batches: newPickedBatchData,
        batchCountry: newPickedBatchCountry,
      },
      dispatch,
    )
      .then(res => {
        setCalculations({ results: res, loading: false });
      })
      .catch(err => {
        setCalculations({ results: null, loading: false });
        showToastWarn();
      });
  };

  const brandChangeHandler = (userPickedBrand, newPickedBatchData, newPickedBatchCountry) => {
    setPickedBrand(userPickedBrand);
    calculateHandler(userPickedBrand, chosenFilters, newPickedBatchData, newPickedBatchCountry);
  };

  const onFiltersSubmit = preparedFilterData => {
    setChosenFilters(preparedFilterData);
    calculateHandler(pickedBrand, preparedFilterData, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchQuarter = event => {
    const {
      target: { value },
    } = event;

    // On autofill we get a stringified value.
    const valueToSet = typeof value === 'string' ? value.split(',') : value;
    if (!valueToSet.length) return;

    const batchInfoToSet = { ...pickedBatchData, quarter: valueToSet };

    setPickedBatchData(batchInfoToSet);
  };

  const onConfirmBatchQuarterChange = () => {
    //Run calculation on quarter selector close
    //Run brand algo, this change event cannot trigger if calculation type is buyer
    calculateHandler(pickedBrand, chosenFilters, pickedBatchData, pickedBatchCountry);
  };

  const onChangeBatchCountry = event => {
    const {
      target: { value },
    } = event;

    const newestYearForPickedBrand = Object.keys(appData.yourBrandAndConsumersData[value][pickedBrand])
      .sort()
      .pop();

    const quarters = appData.yourBrandAndConsumersData[value][pickedBrand][newestYearForPickedBrand].sort((a, b) =>
      a.localeCompare(b),
    );
    const latestQuarter = quarters[quarters.length - 1];
    const [quarter, increment] = latestQuarter.split('_');

    setBrandPickedId(`${brandName}/batch_${quarter}_${newestYearForPickedBrand}_${value}_${increment}`);
  };

  const onChangeBatchYear = event => {
    const {
      target: { value },
    } = event;

    const quarters = appData.yourBrandAndConsumersData[pickedBatchCountry][pickedBrand][value].sort((a, b) =>
      a.localeCompare(b),
    );
    const latestQuarter = quarters[quarters.length - 1];
    const [quarter, increment] = latestQuarter.split('_');

    setBrandPickedId(`${brandName}/batch_${quarter}_${value}_${pickedBatchCountry}_${increment}`);
  };

  const showToastWarn = () => {
    toast.warn('Something went wrong, please try again later.', {
      position: 'bottom-right',
      autoClose: 4000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <PageContainer>
      {pageIsLoading || calculations.loading ? <PageLoader dashboardCalculation /> : null}
      {calculations.results ? (
        <div ref={PDFContentRef}>
          <UserDropdownHeader />

          <Box
            width="100%"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            padding="20px 20px 0px 20px"
            style={{ background: '#fff', gap: '30px' }}>
            <Box flex="1">
              <HeaderSelector
                selectedId={brandPickedId ? brandPickedId : `${brandName}/${batch}`}
                setPickedId={setBrandPickedId}
                data={appData}
                type="brandConsumers"
                pickedBatchCountry={pickedBatchCountry}
                redirect
              />
            </Box>
            <Box display="flex" justifyContent="flex-start" alignItems="center">
              <BuyerBatchSelector
                calculationType={'batch'}
                onChangeCalculationType={() => {}}
                pickedBuyerPersona={null}
                onChangeBuyerPersona={() => {}}
                pickedBatchData={pickedBatchData}
                pickedBatchCountry={pickedBatchCountry}
                onChangeBatchCountry={onChangeBatchCountry}
                onChangeBatchQuarter={onChangeBatchQuarter}
                onConfirmBatchQuarterChange={onConfirmBatchQuarterChange}
                onChangeBatchYear={onChangeBatchYear}
                type="brandConsumers"
                customBatchBrandName={pickedBrand ? pickedBrand : brandName}
                withConsumerHeading
              />
            </Box>
          </Box>

          <Filters calculateHandler={onFiltersSubmit} themeColor="green" pickedBatchCountry={pickedBatchCountry} pickedBatchData={pickedBatchData} />
          {/* <PDFExportButton contentRef={PDFContentRef} type="green" /> */}

          {calculations.results.no_data ? (
            <Box display="flex" justifyContent="center" alignItems="center" marginTop="100px">
              <Typography
                variant="h6"
                color="textSecondary"
                style={{
                  fontSize: '20px',
                  margin: '0',
                  padding: '0',
                  textAlign: 'center',
                  fontWeight: 'bold',
                }}>
                No data, choose other "Consumer Settings" combination.
              </Typography>
            </Box>
          ) : (
            <ChartCalculations data={calculations.results} name={brandName} />
          )}
        </div>
      ) : null}
      <ToastContainer
        theme="colored"
        position="bottom-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </PageContainer>
  );
};

export default BrandConsumersDashboard;
